import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import {
  useGetClientAccountAssociation,
  useRemoveClientAccountSite,
  useUpdateClientAccountAssociation
} from '@apps-common/apis/portal/client-accounts'
import { QUERY_KEY_GET_MY_MONITORING_ACCOUNT_UNITS } from '@apps-common/apis/portal/my-monitoring'
import { ButtonRoundedSm, CheckboxInputSM, InputSM } from '@apps-common/components/elements'
import { ConfirmModalAction } from '@apps-common/components/modals'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer,
  ModalTopConfirmActionButton
} from '@apps-common/components/modals/modal'
import { MESSAGE_IS_MM_ADMIN } from '@apps-common/config/app-messages'
import { ModalDismiss } from '@apps-common/config/modal'
import { queryClient } from '@apps-common/lib'
import { IMyMonitoringUserModel } from '@apps-common/types/portal/users'

export const MyMonitoringUnitModifyModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: { userId: number; unitId: string; isEditing: boolean }
}) => {
  const { userId, unitId, isEditing } = componentProps

  const afterChange = (role: string) => {
    if (role === ModalDismiss.Confirm) {
      queryClient.refetchQueries({ queryKey: QUERY_KEY_GET_MY_MONITORING_ACCOUNT_UNITS(userId) })
    }
    onDismiss(null, role)
  }

  const {
    data: association,
    isLoading: associationIsLoading,
    isError: associationIsError
  } = useGetClientAccountAssociation({
    config: { enabled: isEditing },
    userId: userId || 0,
    unitId
  })

  const { mutate: associateDevice, isPending: associateIsPending } = useUpdateClientAccountAssociation(
    true,
    (result: boolean) => {
      if (result) {
        afterChange(ModalDismiss.Confirm)
      }
    },
    'Device added.',
    'Failed to add device.'
  )
  const { mutate: removeDevice, isPending: removeIsPending } = useRemoveClientAccountSite(
    true,
    (result: boolean) => {
      if (result) {
        afterChange(ModalDismiss.Confirm)
      }
    },
    'Device removed.',
    'Failed to remove device.'
  )

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    reset
  } = useForm<IMyMonitoringUserModel>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      id: userId,
      unitId
    }
  })

  useEffect(() => {
    if (association) {
      reset(association)
    }
  }, [association, reset])

  const submit = (values: IMyMonitoringUserModel) => {
    if (values.userPartition && values.userPartition.length > 0) {
      values.mapPartition = true
    }

    associateDevice({ model: values })
  }

  const isLoading = isEditing && (associationIsLoading || associationIsError)
  const isUpdating = isSubmitting || associateIsPending || removeIsPending

  return (
    <ModalContainer innerClasses='sm:!min-h-0 !h-auto'>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle='Update' title='User device' />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[100px]'>
        <div className='flex items-center justify-end'>
          {isEditing && (
            <ModalTopConfirmActionButton
              loading={isLoading || isUpdating}
              confirmProps={{
                text: 'Remove device?',
                description: 'Are you sure you would like to remove device from this account?',
                action: ConfirmModalAction.Error
              }}
              confirmCallback={() => removeDevice({ userId, unitId })}
              text='Remove'
              icon='delete'
              classes='action-error'
            />
          )}
        </div>
        <form id='my-monitoring-user-device-update-form' noValidate onSubmit={handleSubmit(submit)}>
          <InputSM
            key='userNumber'
            type='text'
            placeholder='1 to 999'
            label='User number (Zone)'
            id='userNumber'
            showLabel
            loading={isLoading}
            disabled={isUpdating}
            registration={register('userNumber', {
              required: true,
              pattern: {
                value: /^[0-9]{3}$/,
                message: '3 numerical characters only e.g. 001'
              }
            })}
            errors={errors.userNumber}
          />
          <InputSM
            key='userPartition'
            type='text'
            placeholder='0 to 99'
            label='User partition (If required)'
            id='userPartition'
            showLabel
            loading={isLoading}
            disabled={isUpdating}
            registration={register('userPartition', {
              required: false,
              pattern: {
                value: /^[0-9]{2}$/,
                message: '2 numerical characters only e.g. 01'
              }
            })}
            errors={errors.userPartition}
          />
          <CheckboxInputSM
            label='Site administrator'
            id='isAdmin'
            disabled={isLoading}
            registration={register('isAdmin')}
            errors={errors.isAdmin}
            info={MESSAGE_IS_MM_ADMIN}
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <ButtonRoundedSm
          type='submit'
          form='my-monitoring-user-device-update-form'
          loading={isLoading || isUpdating}
          text='Update'
          className='action-success'
        />
      </ModalFooter>
    </ModalContainer>
  )
}
